import { RefObject, useEffect, useState } from 'react';
import Text from '@atoms/Text/Text';
import Config from '@config';
import Icon, { IconProps } from '@atoms/Icon/Icon';
import { StyledLink, StyledLinkWrapper, StyledSnackBar, StyledTextWrapper } from './SnackBar.styles';
import Validation from '@icons/validation.svg';
import { UrlObject } from 'url';
import CloseSmall from '@icons/close_small.svg';
import Button from '@atoms/Button/Button';
import { useTranslations } from 'next-intl';

export type TIconSnackbar = null | 'valid';

interface Props {
  text: string;
  show: (show: boolean) => void;
  bottomPosition?: number;
  callback?: () => void;
  icon: TIconSnackbar;
  link?: string | UrlObject;
  linkText?: string;
  onClick?: () => void;
  onClose?: () => void;
  ref?: RefObject<HTMLDivElement | null>;
}

const SnackBar = ({ text, show, callback, bottomPosition, icon, link, linkText, onClick, onClose, ref }: Props) => {
  const [visible, setVisibility] = useState<boolean>(true);

  const t = useTranslations('common');
  // Manage visibility and timer for this snackbar
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!link) setVisibility(false);
    }, Config.TIMEOUT.snackBarDestroyMs);

    return () => clearTimeout(timer); // Cleanup timer on unmount
  }, [link]);

  // Handle visibility change and notify parent
  useEffect(() => {
    if (!visible) {
      setTimeout(() => {
        show(false);
        if (callback) callback();
      }, Config.TIMEOUT.snackBarAnimationMs);
    }
  }, [visible, show, callback]);

  // Handle Escape key to close snackbar
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setVisibility(false);
        if (onClose) onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const icons: {
    [key: string]: IconProps;
  } = {
    valid: { svg: Validation, size: 16, color: 'highlight' },
  };

  return (
    <StyledSnackBar $slideIn={visible} $bottomPosition={bottomPosition} ref={ref}>
      <StyledTextWrapper>
        <Text type="body" size="small" color="white">
          {text}
        </Text>
        {icon && icons[icon] && <Icon {...icons[icon]} />}
      </StyledTextWrapper>
      {link && (
        <StyledLinkWrapper>
          <StyledLink
            href={link}
            onClick={(e) => {
              if (onClick) {
                e.preventDefault(); // Prevent navigation
                onClick();
              } else {
                if (onClose) onClose();
                setVisibility(false);
              }
            }}
          >
            {linkText}
          </StyledLink>
          <Button
            title={t('closeSnackbar')}
            aria-label={t('closeSnackbar')}
            variant="transparent"
            onClick={() => {
              if (onClose) onClose();
              setVisibility(false);
            }}
            data-testid="snackbar-close"
          >
            <Icon svg={CloseSmall} size={12} color="white" />
          </Button>
        </StyledLinkWrapper>
      )}
    </StyledSnackBar>
  );
};

export default SnackBar;
